// checkoutTabs.js
import React from "react";
import {
	TbCircleDashedNumber1,
	TbCircleDashedNumber2,
	TbCircleDashedNumber3,
	TbCircleNumber1,
	TbCircleNumber2,
	TbCircleNumber3,
} from "react-icons/tb";

import CheckoutConfigurationForm from "../components/ui/checkout/forms/ConfigureServerForm";
import CheckoutPaymentMethodForm from "../components/ui/checkout/forms/ConfigurePaymentMethodForm";
import CheckoutConfirmForm from "../components/ui/checkout/forms/ConfirmForm";

/**
 * Returns an array of tabs (with requiredFields, etc.) based on the serviceType.
 * @param {string} serviceType - e.g. "vps", "baremetal", "storage", ...
 */

export function getCheckoutTabs(serviceType, translations) {
	switch (serviceType) {
		case "vps":
			return [
				{
					title: translations.CheckoutTabsVPSOneTitle,
					description: translations.CheckoutTabsVPSOneDescription,
					icon: {
						standard: TbCircleNumber1,
						dashed: TbCircleDashedNumber1,
					},
					panelContent: ({ addons, addAddon, removeAddon }) => (
						<CheckoutConfigurationForm addons={addons} addAddon={addAddon} removeAddon={removeAddon} />
					),
					requiredFields: ["configuration.serverName", "configuration.os.id", "configuration.paymentCycle"],
				},
				{
					title: translations.CheckoutTabsVPSTwoTitle,
					description: translations.CheckoutTabsVPSTwoDescription,
					icon: {
						standard: TbCircleNumber2,
						dashed: TbCircleDashedNumber2,
					},
					panelContent: () => <CheckoutPaymentMethodForm />,
					requiredFields: ["configuration.paymentProfile, configuration.paymentMethod"],
				},
				{
					title: translations.CheckoutTabsVPSThreeTitle,
					description: translations.CheckoutTabsVPSThreeDescription,
					icon: {
						standard: TbCircleNumber3,
						dashed: TbCircleDashedNumber3,
					},
					panelContent: ({ addons, pageContext, currentTab, setCurrentTab, orderNumber, totalPrice }) => <CheckoutConfirmForm
						addons={addons}
						pageContext={pageContext}
						currentTab={currentTab}
						setCurrentTab={setCurrentTab}
						orderNumber={orderNumber}
						totalPrice={totalPrice}
					/>,
					requiredFields: [],
				},
			];

		case "colocation":
			return [
				{
					title: translations.CheckoutTabsColocationOneTitle,
					description: translations.CheckoutTabsColocationOneDescription,
					icon: {
						standard: TbCircleNumber1,
						dashed: TbCircleDashedNumber1,
					},
					panelContent: ({ addons, addAddon, removeAddon }) => (
						<CheckoutConfigurationForm addons={addons} addAddon={addAddon} removeAddon={removeAddon} />
					),
					requiredFields: ["configuration.paymentCycle"],
				},
				{
					title: translations.CheckoutTabsColocationTwoTitle,
					description: translations.CheckoutTabsColocationTwoDescription,
					icon: {
						standard: TbCircleNumber2,
						dashed: TbCircleDashedNumber2,
					},
					panelContent: () => <CheckoutPaymentMethodForm />,
					requiredFields: ["configuration.paymentProfile, configuration.paymentMethod"],
				},
				{
					title: translations.CheckoutTabsColocationThreeTitle,
					description: translations.CheckoutTabsColocationThreeDescription,
					icon: {
						standard: TbCircleNumber3,
						dashed: TbCircleDashedNumber3,
					},
					panelContent: ({ orderNumber }) => <CheckoutConfirmForm
						orderNumber={orderNumber} />,
					requiredFields: [],
				},
			];

		case "baremetal":
			return [
				{
					title: translations.CheckoutTabsBaremetalOneTitle,
					description: translations.CheckoutTabsBaremetalOneDescription,
					icon: {
						standard: TbCircleNumber1,
						dashed: TbCircleDashedNumber1,
					},
					panelContent: ({ addons, addAddon, removeAddon }) => (
						<CheckoutConfigurationForm addons={addons} addAddon={addAddon} removeAddon={removeAddon} />
					),
					// Maybe baremetal only requires "serverName" and "paymentCycle"
					requiredFields: ["configuration.serverName", "configuration.paymentCycle"],
				},
				{
					title: translations.CheckoutTabsBaremetalTwoTitle,
					description: translations.CheckoutTabsBaremetalTwoDescription,
					icon: {
						standard: TbCircleNumber2,
						dashed: TbCircleDashedNumber2,
					},
					panelContent: () => <CheckoutPaymentMethodForm />,
					requiredFields: ["configuration.paymentProfile"],
				},
				{
					title: translations.CheckoutTabsBaremetalThreeTitle,
					description: translations.CheckoutTabsBaremetalThreeDescription,
					icon: {
						standard: TbCircleNumber3,
						dashed: TbCircleDashedNumber3,
					},
					panelContent: ({ orderNumber }) => <CheckoutConfirmForm
						orderNumber={orderNumber} />,
					requiredFields: [],
				},
			];

		case "storage":
			return [
				{
					title: translations.CheckoutTabsStorageOneTitle,
					description: translations.CheckoutTabsStorageOneDescription,
					icon: {
						standard: TbCircleNumber1,
						dashed: TbCircleDashedNumber1,
					},
					panelContent: ({ addons, addAddon, removeAddon }) => (
						<CheckoutConfigurationForm addons={addons} addAddon={addAddon} removeAddon={removeAddon} />
					),
					requiredFields: [
						"configuration.serverName",
						"configuration.nas_storage.size",
						"configuration.paymentCycle",
					],
				},
				{
					title: translations.CheckoutTabsStorageTwoTitle,
					description: translations.CheckoutTabsStorageTwoDescription,
					icon: {
						standard: TbCircleNumber2,
						dashed: TbCircleDashedNumber2,
					},
					panelContent: () => <CheckoutPaymentMethodForm />,
					requiredFields: ["configuration.paymentProfile"],
				},
				{
					title: translations.CheckoutTabsStorageThreeTitle,
					description: translations.CheckoutTabsStorageThreeDescription,
					icon: {
						standard: TbCircleNumber3,
						dashed: TbCircleDashedNumber3,
					},
					panelContent: ({ orderNumber }) => <CheckoutConfirmForm orderNumber={orderNumber} />,
					requiredFields: [],
				},
			];

		default:
			// Fallback or minimal if some unknown serviceType
			return [
				{
					title: translations.CheckoutTabsVPSOneTitle,
					description: translations.CheckoutTabsVPSOneDescription,
					icon: {
						standard: TbCircleNumber1,
						dashed: TbCircleDashedNumber1,
					},
					panelContent: ({ addons, addAddon, removeAddon }) => (
						<CheckoutConfigurationForm addons={addons} addAddon={addAddon} removeAddon={removeAddon} />
					),
					requiredFields: ["configuration.serverName", "configuration.paymentCycle"],
				},
				{
					title: translations.CheckoutTabsVPSTwoTitle,
					description: translations.CheckoutTabsVPSTwoDescription,
					icon: {
						standard: TbCircleNumber2,
						dashed: TbCircleDashedNumber2,
					},
					panelContent: () => <CheckoutPaymentMethodForm />,
					requiredFields: ["configuration.paymentProfile"],
				},
				{
					title: translations.CheckoutTabsVPSThreeTitle,
					description: translations.CheckoutTabsVPSThreeDescription,
					icon: {
						standard: TbCircleNumber3,
						dashed: TbCircleDashedNumber3,
					},
					panelContent: ({ orderNumber }) => <CheckoutConfirmForm orderNumber={orderNumber}/>,
					requiredFields: [],
				},
			];
	}
}
